/**
 * Projects Member Dashboard (default ProjectManagement landing page).
 *
 * The personal, self-scoped counterpart to the org-wide admin overview. Every
 * widget is scoped to the authenticated user's own projects, tasks and
 * milestones. Co-located widgets live under `./components/` and own their own
 * queries — mirroring HR's staff dashboard architecture.
 *
 * Layout:
 *   header (avatar + greeting + refresh)
 *   ├── KPI tiles (My Active Projects · My Open Tasks · Upcoming Milestones · At Risk)
 *   ├── full-width alerts (rendered only when there's something to show)
 *   └── two-column grid:
 *       ├── main (~⅔): MyProjects · (MyTasks + UpcomingMilestones) · RecentActivity
 *       └── sidebar (~⅓): MyProjectHealth · QuickActions
 *
 * Every query is keyed under `['projects', 'member', 'dashboard', ...]` so the
 * single header refresh button invalidates every section in one shot.
 */

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Avatar,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@/components/ui';
import { LayoutContainer } from '@/components/ui/layout';
import { FadeIn } from '@/components/animations';
import { IconRefresh } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import {
  MEMBER_DASHBOARD_KEY,
  MySummaryMetrics,
  MyAlertsPanel,
  MyProjectsPanel,
  MyTasksPanel,
  UpcomingMilestonesPanel,
  RecentActivityPanel,
  MyProjectHealthCard,
  QuickActionsPanel,
} from './components';

function greetingForHour(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export default function ProjectsMemberDashboard() {
  const queryClient = useQueryClient();
  const { user } = useEnrichedAuth();

  const greeting = useMemo(() => {
    const base = greetingForHour();
    const firstName = user?.firstName ?? '';
    return firstName ? `${base}, ${firstName}` : base;
  }, [user]);

  const displayName = user?.displayName ?? user?.username ?? '';

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: MEMBER_DASHBOARD_KEY });
  };

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="md" wrap="nowrap">
              <Avatar src={user?.avatar || undefined} size="lg" radius="xl" color="indigo">
                {initials(displayName)}
              </Avatar>
              <Stack gap={2}>
                <Title order={2}>{greeting}</Title>
                <Text size="sm" c="dimmed">
                  Your projects at a glance.
                </Text>
              </Stack>
            </Group>
            <Tooltip label="Refresh dashboard">
              <ActionIcon
                variant="light"
                size="lg"
                radius="xl"
                onClick={handleRefresh}
                aria-label="Refresh"
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* KPI tiles */}
          <MySummaryMetrics />

          {/* Full-width alerts (rendered only when populated) */}
          <MyAlertsPanel />

          {/* Two-column grid */}
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Stack gap="md">
                <MyProjectsPanel />
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <MyTasksPanel />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <UpcomingMilestonesPanel />
                  </Grid.Col>
                </Grid>
                <RecentActivityPanel />
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Stack gap="md">
                <MyProjectHealthCard />
                <QuickActionsPanel />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
